import React from "react";
import SqlResultTableBody from "./SqlResultTableBody";
import { getLoadMoreBatchSize } from "../../../utils/tablePaginationUtils";

const ViewFullIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7" />
  </svg>
);

const CsvIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="7 10 12 15 17 10" />
    <line x1="12" y1="15" x2="12" y2="3" />
  </svg>
);

const SpinnerIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="spin-icon" aria-hidden="true">
    <path d="M21 12a9 9 0 1 1-6.219-8.56" />
  </svg>
);

const ChevronDown = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

function TableExportCsvDropdown({
  messageId,
  totalCount,
  getCsvExportOptions,
  onPickExportOption,
  isOpen,
  onToggle,
  isExportOptionLoading,
  menuPlacement = "above",
}) {
  const menuClass =
    menuPlacement === "above"
      ? "table-export-dropdown-menu table-export-dropdown-menu--above"
      : "table-export-dropdown-menu";

  return (
    <div className="table-export-dropdown-wrapper">
      <button
        type="button"
        onClick={onToggle}
        className="btn-export-csv"
        disabled={!messageId}
        title={!messageId ? "Export is available only for saved messages" : undefined}
      >
        <CsvIcon />
        Export CSV
      </button>

      {isOpen ? (
        <div className={menuClass}>
          {(getCsvExportOptions ? getCsvExportOptions(totalCount) : []).map((opt) => (
            <button
              key={opt.exportType}
              type="button"
              className="table-export-dropdown-item"
              onClick={() => onPickExportOption?.(opt.exportType)}
              disabled={isExportOptionLoading?.(opt.exportType)}
            >
              {opt.label}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function TableFooter({ rawResults, footer }) {
  if (!footer) return null;

  const {
    loadedCount,
    totalCount,
    onOpenFullView,
    onLoadMore,
    sqlQuery,
    hasMore,
    isLoadingMore,
    loadMoreCount: loadMoreCountProp,
    export: exportConfig,
  } = footer;

  const displayCount = rawResults.length;
  const loadMoreCount =
    loadMoreCountProp ?? getLoadMoreBatchSize(displayCount, totalCount);

  return (
    <div className="table-footer">
      <div className="table-footer-info">
        Showing {displayCount.toLocaleString()} rows
        {totalCount != null && totalCount !== "" ? (
          <span> of {Number(totalCount).toLocaleString()} total</span>
        ) : null}
      </div>
      <div className="table-footer-actions">
        <button type="button" onClick={onOpenFullView} className="btn-view-full-table">
          <ViewFullIcon />
          Full View
        </button>

        {exportConfig ? (
          <TableExportCsvDropdown
            messageId={exportConfig.messageId}
            totalCount={totalCount}
            getCsvExportOptions={exportConfig.getCsvExportOptions}
            onPickExportOption={exportConfig.onPickExportOption}
            isOpen={exportConfig.isOpen}
            onToggle={exportConfig.onToggle}
            isExportOptionLoading={exportConfig.isExportOptionLoading}
            menuPlacement={exportConfig.menuPlacement ?? "above"}
          />
        ) : null}

        {sqlQuery && hasMore ? (
          <button
            type="button"
            onClick={() => onLoadMore?.()}
            disabled={isLoadingMore || !onLoadMore}
            className="btn-pagination"
          >
            {isLoadingMore ? (
              <>
                <SpinnerIcon />
                Loading...
              </>
            ) : (
              <>
                Load More ({loadMoreCount})
                <ChevronDown />
              </>
            )}
          </button>
        ) : null}
      </div>
    </div>
  );
}

/**
 * SQL result table + toolbar shared by ChatSection and AdminDashboard.
 */
export default function SqlResultDataTable({
  variant,
  rawResults,
  formatScalarForDisplay,
  admin,
  chat,
  footer: footerProp,
  embedInPanel = false,
  sort: sortProp,
}) {
  if (!rawResults || rawResults.length === 0) return null;

  let footer = footerProp;

  if (!footer && variant === "admin") {
    const {
      onOpenFullView,
      totalCount,
      export: exportConfig,
      sqlQuery,
      hasMore,
      isLoadingMore,
      loadMoreCount,
      onLoadMore,
      loadedCount,
    } = admin || {};
    footer = {
      loadedCount: loadedCount ?? rawResults.length,
      totalCount,
      onOpenFullView,
      onLoadMore,
      sqlQuery,
      hasMore,
      isLoadingMore,
      loadMoreCount,
      export: exportConfig,
    };
  }

  if (!footer && variant === "chat") {
    const {
      sqlQuery,
      messageId,
      loadedCount,
      totalCount,
      hasMore,
      isLoadingMore,
      loadMoreCount,
      onOpenFullView,
      onLoadMore,
      getCsvExportOptions,
      onPickExportOption,
      activeExportDropdown,
      exportDropdownKey,
      onToggleExportDropdown,
      isExportOptionLoading,
    } = chat || {};

    footer = {
      sqlQuery,
      loadedCount,
      totalCount,
      hasMore,
      isLoadingMore,
      loadMoreCount,
      onOpenFullView,
      onLoadMore,
      export: {
        messageId,
        getCsvExportOptions,
        onPickExportOption,
        isOpen: activeExportDropdown === exportDropdownKey,
        onToggle: onToggleExportDropdown,
        isExportOptionLoading,
        menuPlacement: "above",
      },
    };
  }

  let sort = sortProp;
  if (!sort && variant === "admin") {
    sort = admin?.sort;
  }
  if (!sort && variant === "chat") {
    sort = chat?.sort;
  }

  return (
    <SqlResultTableBody
      rawResults={rawResults}
      formatScalarForDisplay={formatScalarForDisplay}
      wrapperMode={embedInPanel ? "panel" : "message"}
      renderFooter={<TableFooter rawResults={rawResults} footer={footer} />}
      sort={sort}
    />
  );
}

